import { normalizeDayKey } from './tableReservationEligibility';
import { slotLabel, statusForTableSlot, type TableSlotStatus } from './tableMapUtils';
import type { VenueConfig, VenueTable } from '../types';

export type SlotNorm = {
  day: string;
  start_time: string;
  end_time: string;
} | null;

/**
 * Zeus / venue-config default: omit time picking unless explicitly `false`.
 * Also accepts seats payload top-level `omit_table_time_slots`.
 */
export function shouldOmitTableTimeSlots(
  venueConfig?: Pick<VenueConfig, 'omit_table_time_slots'> | null,
  seatPayload?: unknown,
): boolean {
  if (venueConfig && typeof venueConfig.omit_table_time_slots === 'boolean') {
    return venueConfig.omit_table_time_slots;
  }
  if (seatPayload && typeof seatPayload === 'object') {
    const o = seatPayload as Record<string, unknown>;
    const data =
      o.data && typeof o.data === 'object'
        ? (o.data as Record<string, unknown>)
        : null;
    const flag = o.omit_table_time_slots ?? data?.omit_table_time_slots;
    if (typeof flag === 'boolean') return flag;
  }
  return true;
}

export function slotsEqual(a: SlotNorm, b: SlotNorm): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return (
    normalizeDayKey(a.day) === normalizeDayKey(b.day) &&
    a.start_time === b.start_time &&
    a.end_time === b.end_time
  );
}

export function maxPartyFromTables(
  tablesWithSlots: Array<{ table: { chairs?: number } }>,
): number {
  let m = 1;
  for (const { table } of tablesWithSlots) {
    const c = Number(table.chairs) || 1;
    if (c > m) m = c;
  }
  return m;
}

export type SlotOption = { key: string; slot: SlotNorm; label: string };

/** Start time as HH:MM (24h) for chip UI, e.g. 18:00 */
function timeShort24(startTime: string | undefined): string {
  if (!startTime) return '—';
  const m = /^(\d{1,2}):(\d{2})/.exec(String(startTime).trim());
  if (!m) return startTime;
  return `${m[1].padStart(2, '0')}:${m[2]}`;
}

function hourFromStartTime(startTime: string | undefined): number {
  if (!startTime) return 12;
  const m = /^(\d{1,2}):/.exec(String(startTime).trim());
  return m ? parseInt(m[1], 10) : 12;
}

/**
 * “Choose for me” chip: main time + sublabel (aligns with meeteor web large card).
 */
export function slotChipMeta(slot: SlotNorm): { main: string; sub: string } {
  if (!slot) {
    return { main: 'OPEN', sub: 'SEATING' };
  }
  const h = hourFromStartTime(slot.start_time);
  // Align labels with meeteor web: earlier windows vs peak vs late
  const sub = h < 17 ? 'OFF PEAK' : h < 22 ? 'PEAK HOURS' : 'LATE NIGHT';
  return { main: timeShort24(slot.start_time), sub };
}

export function buildSlotOptionsForTables(
  tablesWithSlots: Array<{ table: unknown; slots: SlotNorm[] }>,
): SlotOption[] {
  const map = new Map<string, SlotNorm>();
  for (const { slots } of tablesWithSlots) {
    for (const slot of slots) {
      if (slot === null) {
        map.set('open', null);
      } else {
        const k = `${normalizeDayKey(slot.day)}|${slot.start_time}|${slot.end_time}`;
        map.set(k, slot);
      }
    }
  }
  const timed: { key: string; slot: NonNullable<SlotNorm> }[] = [];
  for (const [key, slot] of Array.from(map.entries())) {
    if (key === 'open') continue;
    if (slot) timed.push({ key, slot });
  }
  timed.sort((a, b) => {
    const aT = a.slot.start_time || '';
    const bT = b.slot.start_time || '';
    return aT.localeCompare(bT);
  });
  const out: SlotOption[] = [];
  if (map.has('open')) {
    out.push({ key: 'open', slot: null, label: 'Open seating' });
  }
  for (const t of timed) {
    out.push({ key: t.key, slot: t.slot, label: slotLabel(t.slot) });
  }
  return out;
}

export type HoldTableLike = {
  table_id: string;
  chairs: number;
  time_slot: { day: string; start_time: string; end_time: string } | null;
};

export function pickSmallestFittingTable(
  tablesWithSlots: Array<{ table: VenueTable; slots: SlotNorm[] }>,
  partyCount: number,
  chosenSlot: SlotNorm,
  availability: Record<string, string>,
): HoldTableLike | null {
  type Cand = { table: VenueTable; slot: SlotNorm; cap: number };
  const cands: Cand[] = [];
  for (const { table, slots } of tablesWithSlots) {
    const cap = Number(table.chairs) || 1;
    if (cap < partyCount) continue;
    for (const slot of slots) {
      if (chosenSlot === null) {
        if (slot !== null) continue;
      } else {
        if (slot === null) continue;
        if (!slotsEqual(chosenSlot, slot)) continue;
      }
      const tid = String(table.id);
      const st = statusForTableSlot(
        tid,
        slot,
        availability,
      ) as TableSlotStatus;
      if (st === 'sold' || st === 'held' || st === 'blocked') continue;
      cands.push({ table, slot, cap });
    }
  }
  if (cands.length === 0) return null;
  cands.sort((a, b) => a.cap - b.cap);
  const best = cands[0];
  const tid = String(best.table.id);
  const chairs = Math.min(
    partyCount,
    Number(best.table.chairs) || 1,
  );
  return {
    table_id: tid,
    chairs,
    time_slot: best.slot
      ? {
          day: best.slot.day,
          start_time: best.slot.start_time,
          end_time: best.slot.end_time,
        }
      : null,
  };
}
